import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Vulnerability } from "@/types/vulnerability";
import { ExternalLink, AlertTriangle, Calendar, Clock, Shield } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { AppleDeviceIcon } from "@/components/icons";
import { ScrollArea } from "@/components/ui/scroll-area";

interface VulnerabilityDetailPanelProps {
  vulnerability: Vulnerability | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VulnerabilityDetailPanel({ vulnerability, open, onOpenChange }: VulnerabilityDetailPanelProps) {
  if (!vulnerability) return null;

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-destructive text-destructive-foreground';
      case 'HIGH':
        return 'bg-[hsl(30_80%_55%)] text-white';
      case 'MEDIUM':
        return 'bg-[hsl(45_90%_55%)] text-black';
      case 'LOW':
        return 'bg-[hsl(220_70%_50%)] text-white';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const daysAgo = Math.floor(
    (new Date().getTime() - new Date(vulnerability.published_date).getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <SheetTitle className="text-2xl font-mono">{vulnerability.cve_id}</SheetTitle>
              <a
                href={`https://nvd.nist.gov/vuln/detail/${vulnerability.cve_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:underline flex items-center gap-1 mt-1"
              >
                View on NVD <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </SheetHeader>

        {/* Description */}
        <div className="mt-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {vulnerability.description || "No description available."}
          </p>
        </div>

        {/* Quick Metrics */}
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              Severity
            </div>
            <Badge className={`${getSeverityColor(vulnerability.severity)} text-base`}>
              {vulnerability.severity}
            </Badge>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              CVSS Score
            </div>
            <div className="text-2xl font-bold">{vulnerability.cvss_score.toFixed(1)}<span className="text-sm text-muted-foreground">/10</span></div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              Known Exploit
            </div>
            <div className="text-base font-semibold">
              {vulnerability.actively_exploited ? (
                <span className="text-[hsl(30_80%_55%)]">Yes - Active</span>
              ) : (
                <span className="text-muted-foreground">No</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Age
            </div>
            <div className="text-base font-semibold">{daysAgo} days</div>
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Timeline</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Published</span>
              <span className="font-medium">{format(new Date(vulnerability.published_date), 'MMM d, yyyy')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Last Detected</span>
              <span className="font-medium">{formatDistanceToNow(new Date(vulnerability.published_date), { addSuffix: true })}</span>
            </div>
          </div>
        </div>

        {/* Tabbed Content */}
        <Tabs defaultValue="updates" className="mt-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="updates">Affected Updates</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>

          <TabsContent value="updates" className="space-y-3 mt-4">
            <ScrollArea className="h-[300px] pr-4">
              {vulnerability.affected_updates.map((update, idx) => (
                <div
                  key={idx}
                  className="p-4 border rounded-lg mb-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <AppleDeviceIcon osType={update.os_type} className="h-5 w-5 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium">{update.name}</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Version {update.version} • Released {format(new Date(update.released), 'MMM d, yyyy')}
                        </div>
                      </div>
                    </div>
                    <a
                      href={update.notes_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="insights" className="space-y-4 mt-4">
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold mb-2">Patch Availability</h4>
                <p className="text-sm text-muted-foreground">
                  {vulnerability.affected_updates.length} update{vulnerability.affected_updates.length !== 1 ? 's' : ''} available that address this vulnerability.
                  Apply the latest updates to remediate this issue.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Risk Assessment</h4>
                <p className="text-sm text-muted-foreground">
                  {vulnerability.severity === 'CRITICAL' && 
                    "This is a critical vulnerability that should be addressed immediately. It poses a significant security risk."
                  }
                  {vulnerability.severity === 'HIGH' && 
                    "This is a high severity vulnerability that should be prioritized for patching."
                  }
                  {vulnerability.severity === 'MEDIUM' && 
                    "This is a medium severity vulnerability that should be addressed in your regular patching cycle."
                  }
                  {vulnerability.severity === 'LOW' && 
                    "This is a low severity vulnerability. Monitor and patch during routine maintenance."
                  }
                </p>
              </div>

              {vulnerability.actively_exploited && (
                <div className="p-4 bg-[hsl(30_80%_55%)]/10 border border-[hsl(30_80%_55%)]/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-[hsl(30_80%_55%)] mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-[hsl(30_80%_55%)]">Actively Exploited</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        This vulnerability is being actively exploited in the wild. Immediate patching is strongly recommended.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-semibold mb-2">Remediation Steps</h4>
                <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                  <li>Review the affected updates and determine applicability to your devices</li>
                  <li>Test the updates in a non-production environment</li>
                  <li>Deploy updates to affected devices through your patch management policy</li>
                  <li>Verify successful installation and monitor for issues</li>
                </ol>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
